// =============================================
// MODAL DE GESTIÓN DE CATEGORÍAS
// Permite CRUD completo de categorías de gastos
// =============================================

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Box,
  Typography,
  Alert,
  Snackbar,
  CircularProgress,
  Chip,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import expenseCategoryService from '../../services/expenseCategoryService';

const CategoryModal = ({ open, onClose, onUpdate }) => {
  // GASTOS: Estados del componente
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formMode, setFormMode] = useState(null); // null, 'create', 'edit'
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // GASTOS: Estado del formulario
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    is_active: true,
  });

  // GASTOS: Cargar categorías al abrir
  useEffect(() => {
    if (open) {
      loadCategories();
    }
  }, [open]);

  // GASTOS: Función para cargar categorías
  
  const loadCategories = async () => {
    try {
      setLoading(true);
      const response = await expenseCategoryService.getActive();
      //const response = await expenseCategoryService.getAll();
      setCategories(response.categories || []);
    } catch (error) {
      console.error('Error al cargar categorías:', error);
      setSnackbar({
        open: true,
        message: 'Error al cargar categorías',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  // GASTOS: Abrir formulario para crear
  const handleCreate = () => {
    setFormMode('create');
    setSelectedCategory(null);
    setFormData({ name: '', description: '', is_active: true });
  };

  // GASTOS: Abrir formulario para editar
  const handleEdit = (category) => {
    setFormMode('edit');
    setSelectedCategory(category);
    setFormData({
      name: category.name,
      description: category.description || '',
      is_active: category.is_active,
    });
  };

  // GASTOS: Cancelar formulario
  const handleCancel = () => {
    setFormMode(null);
    setSelectedCategory(null);
    setFormData({ name: '', description: '', is_active: true });
  };

  // GASTOS: Guardar categoría (crear o editar)
  const handleSave = async () => {
    try {
      if (!formData.name.trim()) {
        setSnackbar({
          open: true,
          message: 'El nombre es requerido',
          severity: 'error',
        });
        return;
      }

      setLoading(true);

      if (formMode === 'create') {
        await expenseCategoryService.create(formData);
        setSnackbar({
          open: true,
          message: 'Categoría creada exitosamente',
          severity: 'success',
        });
      } else {
        await expenseCategoryService.update(selectedCategory.id, formData);
        setSnackbar({
          open: true,
          message: 'Categoría actualizada exitosamente',
          severity: 'success',
        });
      }

      handleCancel();
      loadCategories();
      onUpdate(); // Notificar al padre que hubo cambios

    } catch (error) {
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Error al guardar categoría',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  // GASTOS: Eliminar categoría
  const handleDelete = async (category) => {
    if (window.confirm(`¿Estás seguro de eliminar la categoría "${category.name}"?`)) {
      try {
        setLoading(true);
        await expenseCategoryService.delete(category.id);
        setSnackbar({
          open: true,
          message: 'Categoría eliminada exitosamente',
          severity: 'success',
        });
        loadCategories();
        onUpdate();
      } catch (error) {
        setSnackbar({
          open: true,
          message: error.response?.data?.message || 'Error al eliminar categoría',
          severity: 'error',
        });
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Gestión de Categorías</Typography>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          {/* GASTOS: Formulario de crear/editar */}
          {formMode && (
            <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="subtitle1" sx={{ mb: 2 }}>
                {formMode === 'create' ? 'Nueva Categoría' : 'Editar Categoría'}
              </Typography>
              
              <TextField
                fullWidth
                label="Nombre"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                sx={{ mb: 2 }}
                required
              />
              
              <TextField
                fullWidth
                label="Descripción"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                multiline
                rows={2}
                sx={{ mb: 2 }}
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  />
                }
                label="Activo"
              />

              <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                <Button onClick={handleCancel} disabled={loading}>
                  Cancelar
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSave}
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={24} /> : 'Guardar'}
                </Button>
              </Box>
            </Box>
          )}

          {/* GASTOS: Botón para crear nueva categoría */}
          {!formMode && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreate}
              sx={{ mb: 2 }}
            >
              Nueva Categoría
            </Button>
          )}

          {/* GASTOS: Tabla de categorías */}
          {loading && !formMode ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Nombre</TableCell>
                    <TableCell>Descripción</TableCell>
                    <TableCell align="center">Estado</TableCell>
                    <TableCell align="right">Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {categories.map((category) => (
                    <TableRow key={category.id}>
                      <TableCell>{category.name}</TableCell>
                      <TableCell>{category.description}</TableCell>
                      <TableCell align="center">
                        <Chip
                          label={category.is_active ? 'Activo' : 'Inactivo'}
                          color={category.is_active ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={() => handleEdit(category)}
                          disabled={formMode !== null}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDelete(category)}
                          disabled={formMode !== null}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} variant="contained">
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      {/* GASTOS: Notificaciones */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default CategoryModal;
